import { useState, useEffect } from "react";
import { ApiClient, getApiErrorMessage } from "@/services/api";
import { Card, Button, Alert, LoadingSpinner } from "@/components/shared";
import { formatDate } from "@/utils/format";

interface SubscriptionData {
  id: string;
  tier: string;
  status: string;
  regions: string[];
  lead_limit: number;
  leads_used: number;
  leads_remaining: number;
  start_date: string;
  end_date: string;
  current_period_start?: string | null;
  current_period_end?: string | null;
  scheduled_tier?: string | null;
}

const PLAN_INFO = {
  free: { name: "Free", price: "R0/mo", priceCents: 0, regions: 1, leads: 0, description: "Browse only" },
  starter: { name: "Starter", price: "R99/mo", priceCents: 9900, regions: 1, leads: 10, description: "1 region, 10 leads/mo" },
  professional: { name: "Professional", price: "R199/mo", priceCents: 19900, regions: 3, leads: 30, description: "3 regions, 30 leads/mo" },
};

const TIER_ORDER: Record<string, number> = { free: 0, starter: 1, professional: 2 };

const PROVINCES = [
  { value: "gauteng", label: "Gauteng" },
  { value: "western_cape", label: "Western Cape" },
  { value: "kwazulu_natal", label: "KwaZulu-Natal" },
  { value: "eastern_cape", label: "Eastern Cape" },
  { value: "free_state", label: "Free State" },
  { value: "limpopo", label: "Limpopo" },
  { value: "mpumalanga", label: "Mpumalanga" },
  { value: "north_west", label: "North West" },
  { value: "northern_cape", label: "Northern Cape" },
];

interface PlanChangeResult {
  action: string;
  authorization_url?: string;
  proration_cents?: number;
  proration_display?: string;
  scheduled_tier?: string;
  effective_date?: string;
  tier?: string;
}

function getRegionLabel(region: string) {
  return PROVINCES.find((province) => province.value === region)?.label ?? region.replace(/_/g, " ");
}

export function requiresDowngradeRegionSelection(subscription: SubscriptionData, newTier: string) {
  const nextPlan = PLAN_INFO[newTier as keyof typeof PLAN_INFO];
  if (!nextPlan) return false;

  return TIER_ORDER[newTier] < TIER_ORDER[subscription.tier] && nextPlan.regions < subscription.regions.length;
}

export function applyScheduledDowngrade(subscription: SubscriptionData, result: PlanChangeResult): SubscriptionData {
  return {
    ...subscription,
    scheduled_tier: result.scheduled_tier ?? subscription.scheduled_tier,
    current_period_end: result.effective_date ?? subscription.current_period_end,
  };
}

export function SubscriptionManager() {
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTier, setSelectedTier] = useState<string>("starter");
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [subscribing, setSubscribing] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [pendingDowngradeTier, setPendingDowngradeTier] = useState<string | null>(null);
  const [downgradeKeepRegions, setDowngradeKeepRegions] = useState<string[]>([]);

  useEffect(() => {
    const fetchSub = async () => {
      try {
        const api = new ApiClient();
        const data = await api.get<{ subscription: SubscriptionData | null }>("/subscriptions/me");
        setSubscription(data.subscription);
      } catch (err: any) {
        // 404 = no subscription yet, not an error
        if (!err.message?.includes("404")) setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchSub();
  }, []);

  const handleSubscribe = async () => {
    if (selectedTier === "free") return; // Free doesn't need payment
    setSubscribing(true);
    setError(null);
    try {
      const api = new ApiClient();
      const result = await api.post<{ authorization_url: string }>("/subscriptions", {
        tier: selectedTier,
        regions: selectedRegions,
      });
      window.location.href = result.authorization_url;
    } catch (err: any) {
      setError(err.message);
      setSubscribing(false);
    }
  };

  const startPlanChange = (newTier: string) => {
    if (subscription && requiresDowngradeRegionSelection(subscription, newTier)) {
      const regionLimit = PLAN_INFO[newTier as keyof typeof PLAN_INFO].regions;
      setPendingDowngradeTier(newTier);
      setDowngradeKeepRegions(subscription.regions.slice(0, regionLimit));
      setError(null);
      return;
    }

    void handlePlanChange(newTier);
  };

  const handlePlanChange = async (newTier: string, keepRegions?: string[]) => {
    setSubscribing(true);
    setError(null);
    try {
      const api = new ApiClient();
      const result = await api.patch<PlanChangeResult>(
        "/subscriptions/me",
        keepRegions ? { tier: newTier, keepRegions } : { tier: newTier }
      );

      if (result.action === "upgrade_payment_required" && result.authorization_url) {
        window.location.href = result.authorization_url;
      } else if (result.action === "upgraded") {
        window.location.reload();
      } else if (result.action === "downgrade_scheduled") {
        setSubscription((current) => current ? applyScheduledDowngrade(current, result) : current);
        setPendingDowngradeTier(null);
        setDowngradeKeepRegions([]);
        setSubscribing(false);
      }
    } catch (err: any) {
      setError(getApiErrorMessage(err));
      setSubscribing(false);
    }
  };

  const toggleDowngradeRegion = (region: string) => {
    if (!pendingDowngradeTier) return;

    const regionLimit = PLAN_INFO[pendingDowngradeTier as keyof typeof PLAN_INFO].regions;
    setDowngradeKeepRegions((prev) => {
      if (prev.includes(region)) return prev.filter((r) => r !== region);
      if (prev.length >= regionLimit) return prev;
      return [...prev, region];
    });
  };

  const handleCancel = async () => {
    setSubscribing(true);
    setError(null);
    try {
      const api = new ApiClient();
      await api.delete("/subscriptions/me");
      window.location.reload();
    } catch (err: any) {
      setError(err.message);
      setSubscribing(false);
    }
  };

  const toggleRegion = (region: string) => {
    const plan = PLAN_INFO[selectedTier as keyof typeof PLAN_INFO];
    setSelectedRegions((prev) => {
      if (prev.includes(region)) return prev.filter((r) => r !== region);
      if (prev.length >= plan.regions) return prev;
      return [...prev, region];
    });
  };

  if (loading) return <LoadingSpinner />;

  // Show current subscription
  if (subscription && subscription.status === "active") {
    const leadPercent = subscription.lead_limit > 0
      ? Math.round((subscription.leads_used / subscription.lead_limit) * 100)
      : 0;
    const renewalDate = subscription.current_period_end
      ? formatDate(subscription.current_period_end)
      : formatDate(subscription.end_date);

    return (
      <Card>
        <h3 className="font-bold text-white mb-3">Current Subscription</h3>

        {/* Scheduled downgrade notice */}
        {subscription.scheduled_tier && (
          <div className="bg-amber-900/30 border border-amber-500/30 rounded-lg p-3 mb-3">
            <p className="text-sm text-amber-200">
              Your plan will change to <span className="font-semibold capitalize">{subscription.scheduled_tier}</span> on {renewalDate}
            </p>
          </div>
        )}

        <div className="bg-teal-900/30 border border-teal-500/30 rounded-lg p-4 mb-3">
          <div className="flex justify-between items-center">
            <div>
              <p className="font-semibold text-teal-300 capitalize">{subscription.tier} Plan</p>
              <p className="text-sm text-teal-400/70">Renews {renewalDate}</p>
            </div>
            <span className="px-3 py-1 bg-green-900/40 text-green-400 rounded-full text-sm font-medium">
              Active
            </span>
          </div>
        </div>

        {/* Lead usage bar */}
        {subscription.lead_limit > 0 && (
          <div className="mb-3">
            <div className="flex justify-between text-sm text-white/70 mb-1">
              <span>Leads used</span>
              <span>{subscription.leads_used} of {subscription.lead_limit}</span>
            </div>
            <div className="w-full bg-white/10 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all ${leadPercent >= 90 ? "bg-red-500" : leadPercent >= 70 ? "bg-amber-500" : "bg-teal-500"}`}
                style={{ width: `${Math.min(100, leadPercent)}%` }}
              />
            </div>
          </div>
        )}

        {subscription.tier === "free" && (
          <div className="bg-white/5 rounded-lg p-3 mb-3">
            <p className="text-sm text-white/70">Free tier — browse only, no contact unlocks. Upgrade to start connecting with sellers.</p>
          </div>
        )}

        <div>
          <p className="text-sm text-white/50 mb-1">Regions</p>
          <div className="flex flex-wrap gap-2">
            {subscription.regions.map((r) => (
              <span key={r} className="px-2 py-1 bg-white/10 rounded text-sm capitalize">
                {r.replace(/_/g, " ")}
              </span>
            ))}
          </div>
        </div>

        {/* Plan change options */}
        {error && <Alert type="error" className="mt-3">{error}</Alert>}
        <div className="mt-4 border-t border-white/10 pt-3">
          <p className="text-sm text-white/50 mb-2">Change plan</p>
          <div className="flex gap-2">
            {Object.entries(PLAN_INFO)
              .filter(([key]) => key !== subscription.tier)
              .map(([key, plan]) => (
                <Button
                  key={key}
                  variant="secondary"
                  onClick={() => startPlanChange(key)}
                  disabled={subscribing}
                  className="text-sm"
                >
                  {TIER_ORDER[key] > TIER_ORDER[subscription.tier] ? "Upgrade" : "Downgrade"} to {plan.name}
                </Button>
              ))}
          </div>

          {pendingDowngradeTier && (
            <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-900/20 p-3">
              <p className="text-sm font-semibold text-amber-200">
                Downgrade to {PLAN_INFO[pendingDowngradeTier as keyof typeof PLAN_INFO].name}
              </p>
              <p className="mt-1 text-sm text-white/60">
                This plan keeps {PLAN_INFO[pendingDowngradeTier as keyof typeof PLAN_INFO].regions} region. Choose which covered region remains active until the scheduled change.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {subscription.regions.map((region) => (
                  <button
                    key={region}
                    type="button"
                    onClick={() => toggleDowngradeRegion(region)}
                    className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
                      downgradeKeepRegions.includes(region)
                        ? "border-amber-400 bg-amber-500 text-midnight"
                        : "border-white/15 bg-midnight text-white/70 hover:border-amber-400/60"
                    }`}
                  >
                    {getRegionLabel(region)}
                  </button>
                ))}
              </div>
              <div className="mt-3 flex gap-2">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setPendingDowngradeTier(null);
                    setDowngradeKeepRegions([]);
                  }}
                  disabled={subscribing}
                  className="text-sm"
                >
                  Keep Current Plan
                </Button>
                <Button
                  variant="primary"
                  onClick={() => handlePlanChange(pendingDowngradeTier, downgradeKeepRegions)}
                  disabled={
                    subscribing ||
                    downgradeKeepRegions.length !== PLAN_INFO[pendingDowngradeTier as keyof typeof PLAN_INFO].regions
                  }
                  className="text-sm"
                >
                  {subscribing ? <LoadingSpinner size="sm" /> : "Schedule Downgrade"}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Cancel subscription (only for paid tiers) */}
        {subscription.tier !== "free" && !subscription.scheduled_tier && (
          <div className="mt-3 border-t border-white/10 pt-3">
            {showCancelConfirm ? (
              <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-3">
                <p className="text-sm text-red-200 mb-2">
                  You'll retain access until {renewalDate}. After that, your plan reverts to Free.
                </p>
                <div className="flex gap-2">
                  <Button variant="secondary" onClick={() => setShowCancelConfirm(false)} className="text-sm">
                    Keep Plan
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={handleCancel}
                    disabled={subscribing}
                    className="text-sm text-red-400 border-red-500/30 hover:bg-red-900/30"
                  >
                    Confirm Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowCancelConfirm(true)}
                className="text-sm text-white/40 hover:text-red-400 transition-colors"
              >
                Cancel subscription
              </button>
            )}
          </div>
        )}
      </Card>
    );
  }

  // Show plan selection
  return (
    <div className="space-y-4">
      <h3 className="font-bold text-white">Choose a Plan</h3>

      {error && <Alert type="error">{error}</Alert>}

      <div className="grid gap-3 md:grid-cols-3">
        {Object.entries(PLAN_INFO).map(([key, plan]) => (
          <Card
            key={key}
            className={`cursor-pointer transition-all h-full ${selectedTier === key ? "ring-2 ring-teal-500 bg-teal-900/30" : "hover:border-gold/30"}`}
            onClick={() => { setSelectedTier(key); setSelectedRegions([]); }}
          >
            <h4 className="font-semibold text-white">{plan.name}</h4>
            <p className="text-2xl font-bold text-sun my-2">{plan.price}</p>
            <ul className="text-sm text-white/60 space-y-1">
              <li>• {plan.regions} region{plan.regions > 1 ? "s" : ""}</li>
              <li>• {plan.leads === 0 ? "No contact unlocks" : `${plan.leads} leads/mo`}</li>
              {key === "free" && <li>• Browse & preview only</li>}
            </ul>
          </Card>
        ))}
      </div>

      {/* Region selection (only for paid tiers) */}
      {selectedTier !== "free" && (
        <div>
          <p className="text-sm font-medium text-white/70 mb-2">
            Select regions ({selectedRegions.length}/{PLAN_INFO[selectedTier as keyof typeof PLAN_INFO].regions})
          </p>
          <div className="flex flex-wrap gap-2">
            {PROVINCES.map((p) => (
              <button
                key={p.value}
                onClick={() => toggleRegion(p.value)}
                className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                  selectedRegions.includes(p.value)
                    ? "bg-teal-500 text-white border-teal-500"
                    : "bg-midnight text-white/70 border-white/15 hover:border-gold/40"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <Button
        variant="primary"
        onClick={handleSubscribe}
        disabled={subscribing || (selectedTier !== "free" && selectedRegions.length === 0)}
        className="w-full"
      >
        {subscribing ? <LoadingSpinner size="sm" /> : selectedTier === "free" ? "Activate Free Plan" : "Subscribe & Pay"}
      </Button>
    </div>
  );
}
